import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

// Centralized unread + blinking control for chat notifications
// - Opening chat zeroes unread and suppresses blinking
// - Blinking is re-enabled only when a brand-new message INSERT arrives for the recipient
export const useChatNotifications = (recipientProfileId?: string | null) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [suppressed, setSuppressed] = useState<boolean>(() => {
    try { return localStorage.getItem("chatSuppressUnread") === "true"; } catch { return false; }
  });

  // Persist suppression state
  useEffect(() => {
    try { localStorage.setItem("chatSuppressUnread", suppressed ? "true" : "false"); } catch {}
  }, [suppressed]);

  // When chat opens, reset unread and suppress blinking
  useEffect(() => {
    if (isChatOpen) {
      setSuppressed(true);
      setUnreadCount(0);
    }
  }, [isChatOpen]);

  // Re-enable blinking only on a brand-new message for this recipient
  useEffect(() => {
    if (!recipientProfileId) return;
    const channel = supabase
      .channel(`chat-unread-realtime-${recipientProfileId}-${crypto.randomUUID()}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "messages",
        filter: `recipient_id=eq.${recipientProfileId}`,
      }, () => {
        setSuppressed(false);
        // If chat is not open, let UI decide whether to fetch or increment
        if (!isChatOpen) setUnreadCount((c) => c + 1);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [recipientProfileId, isChatOpen]);

  const setChatOpen = useCallback((open: boolean) => {
    setIsChatOpen(open);
    if (open) {
      // Defensive: ensure suppression and zero when opened via buttons
      setSuppressed(true);
      setUnreadCount(0);
    }
  }, []);

  const shouldBlink = useMemo(() => !isChatOpen && !suppressed && unreadCount > 0, [isChatOpen, suppressed, unreadCount]);

  return {
    unreadCount,
    setUnreadCount,
    shouldBlink,
    setChatOpen,
    isChatOpen,
    suppressed,
    setSuppressed,
  };
};